import { ConnectionStatusTypes } from 'shared-worker-versioning/lib/types/ConnectionStatuses';
import { getAdditionalRegistrationInformation } from './utils/getAdditionalRegistrationInformation';
import { GENERIC_WORKER_ERROR } from 'shared-worker-versioning/lib/host/utils/connectSharedWorkerPort';
import CrossTabConfig from './calling-cross-tab-utils/CrossTabConfig';
import { logDebugMessage } from './calling-cross-tab-utils/getCrossTabEmbedDebugger';
import { ClientType } from './types/ClientTypes';
import { SHARED_WORKER_URL } from './calling-cross-tab-utils/urls';
import { logError } from 'calling-cross-tab-library/utils/logError';
import { WEB_WORKER_NAME, WEB_WORKER_VERSION } from 'calling-cross-tab-library/constants/WebWorkerVersion';
import CallingCrossTabClientError from 'calling-cross-tab-library/utils/CallingCrossTabClientError';
import AppDataManager from './calling-cross-tab-utils/AppDataManager';
import { onPollingBroadcastMessage, onWorkerMessage } from './utils/onWorkerMessage';
import { getMetricsFactory } from './calling-cross-tab-utils/Metrics';
import { VersionedSharedWorkerHost } from 'shared-worker-versioning/VersionedSharedWorkerHost';
import PortalIdParser from 'PortalIdParser';
import { createPollingBroadcastChannel } from 'calling-cross-tab-library/utils/BroadcastChannels';
import { onDisconnect } from './utils/onDisconnect';
import { isBroadcastRelay } from 'calling-cross-tab-library/types/MessageRelayTypes';
import { logClientDebugMessage } from 'calling-cross-tab-library/utils/getClientDebugger';
import { getUnsupportedReason } from './utils/getIsCrossTabMessagingSupported';
import { UnsupportedReason } from 'calling-cross-tab-library/constants/UnsupportedReason';
export class _WorkerConnection {
  constructor() {
    this.isUnloading = false;
    this.isInitialized = false;
    this.messageClientType = ClientType.SHARED_WORKER;
    this.metrics = undefined;
    this.unsupportedReason = getUnsupportedReason();
    this.handleBroadCastRelayMessage = message => {
      const callingWorkerDetails = CrossTabConfig.get();
      if (callingWorkerDetails.broadcastMessageHandlers.length === 0) return;
      logClientDebugMessage(`Received Message ${message.type}, sending to ${callingWorkerDetails.broadcastMessageHandlers.length} callback(s)`, message);
      callingWorkerDetails.broadcastMessageHandlers.forEach(handler => {
        handler(message);
      });
    };
    this._handleWorkerMessage = (messageEvent, messageChannel) => {
      if (isBroadcastRelay(messageEvent.data)) {
        this.handleBroadCastRelayMessage(messageEvent.data);
      }
      onWorkerMessage(messageEvent, messageChannel);
    };
    this._handleMessageError = (error, extra) => {
      var _this$metrics2;
      if (error && error.message === GENERIC_WORKER_ERROR && (!extra || extra && !extra.message)) {
        var _this$metrics;
        (_this$metrics = this.metrics) === null || _this$metrics === void 0 || _this$metrics.counter('generic-worker-error-received', {
          connectionStatus: this.connectionStatus
        }).increment();
        return;
      }
      (_this$metrics2 = this.metrics) === null || _this$metrics2 === void 0 || _this$metrics2.counter('worker-message-error-received', {
        connectionStatus: this.connectionStatus
      }).increment();
      logDebugMessage(error);
      logError(error, {
        extra
      });
    };
    this.sendMessage = message => {
      if (this._messageClient.connectionStatus !== ConnectionStatusTypes.DISCONNECTING) {
        this._messageClient.sendMessage(message);
        return;
      }
      logError(new CallingCrossTabClientError({
        message: `WorkerConnection#sendMessage called for type ${message.type} while client is ${ConnectionStatusTypes.DISCONNECTING} `
      }), {
        extra: {
          message,
          widgetDetails: AppDataManager.getAppData().widgetDetails
        },
        level: 'info',
        fingerprint: ['WorkerConnection#sendMessage', message.type, ConnectionStatusTypes.DISCONNECTING]
      });
    };
    this.getIsUnsupported = () => {
      if (this.unsupportedReason !== UnsupportedReason.INAPPLICABLE) {
        var _this$metrics3;
        AppDataManager.setConnectionStatus(ConnectionStatusTypes.UNSUPPORTED);
        (_this$metrics3 = this.metrics) === null || _this$metrics3 === void 0 || _this$metrics3.counter('unsupported', {
          reason: this.unsupportedReason
        }).increment();
        return true;
      }
      return false;
    };
    this.handleVersionWorkerStatusUpdate = (connectionStatus, appData) => {
      var _this$metrics4;
      if (appData) {
        AppDataManager.setAppData(appData, connectionStatus);
      } else {
        AppDataManager.setConnectionStatus(connectionStatus);
      }
      logDebugMessage(`Connection status updated: ${connectionStatus}`, appData);
      (_this$metrics4 = this.metrics) === null || _this$metrics4 === void 0 || _this$metrics4.counter('embed-connection-status-update', {
        clientVersion: WEB_WORKER_VERSION,
        appDataVersion: AppDataManager.getAppData().version,
        connectionStatus
      }).increment();
    };
    this._messageClient = new VersionedSharedWorkerHost({
      debug: logDebugMessage,
      portalId: `${PortalIdParser.get()}`,
      version: WEB_WORKER_VERSION,
      name: WEB_WORKER_NAME,
      sharedWorkerUrl: SHARED_WORKER_URL,
      onMessage: this._handleWorkerMessage,
      onVersionWorkerStatusUpdate: this.handleVersionWorkerStatusUpdate,
      onMessageError: this._handleMessageError,
      getAdditionalRegistrationInformation,
      onDisconnect
    });
    window.addEventListener('beforeunload', () => {
      this.isUnloading = true;
    });
  }
  get connectionStatus() {
    return this._messageClient.connectionStatus;
  }
  initialize(connectionType) {
    if (this.isInitialized) return Promise.resolve(AppDataManager.getAppData());
    CrossTabConfig.setConnectionType(connectionType);
    this.isInitialized = true;
    if (this.getIsUnsupported()) return Promise.resolve(new Error('Crosstab is Unsupported'));
    AppDataManager.setConnectionStatus(ConnectionStatusTypes.STARTED);
    this.metrics = getMetricsFactory();
    const connectionId = CrossTabConfig.get().connectionId;
    return this._messageClient.initialize(connectionId).then(appData => {
      var _this$metrics5;
      if (appData) this.broadcastPollingChannel = createPollingBroadcastChannel(connectionId, messageEvent => onPollingBroadcastMessage(messageEvent));
      AppDataManager.setAppData(appData, ConnectionStatusTypes.SUCCEEDED);
      (_this$metrics5 = this.metrics) === null || _this$metrics5 === void 0 || _this$metrics5.counter('initialization-succeeded').increment();
      return appData;
    }).catch(error => {
      var _this$metrics6;
      const InitError = new CallingCrossTabClientError({
        message: `SharedWorkerConnection, ${error.message}`,
        details: {
          error
        }
      });
      InitError.stack = error.stack;
      (_this$metrics6 = this.metrics) === null || _this$metrics6 === void 0 || _this$metrics6.counter('initialization-failure', {
        isRedirect: `${error && error.message && error.message.includes && error.message.includes('redirect')}`,
        isUnloading: `${this.isUnloading}`
      }).increment();
      throw InitError;
    });
  }
}
export default new _WorkerConnection();